import React, {useCallback, useContext, useEffect, useMemo, useState} from "react";
import {ConfigProvider, message} from "antd";
import {TableDateRangeTable} from "../../../components/OrdersDateRangeTable/TableDateRangeTable.jsx";
import {useRequest} from "../../../utils/hooks.jsx";
import {TableDateSearchContext} from "../../../admin/Context/TableDateSearchContext.jsx";
import columns from "../../../admin/AdminOrders/columns.jsx";
import {AppContext} from "../../../AppContext.jsx";
import {
    OrderShortcutFilterValues,
    ORDER_SHORTCUT_SUMMARY_ROOM,
    ORDER_SHORTCUT_SUMMARY_UPDATED_EVENT,
    OrdersShortcuts,
    OpenIssuesShortcut,
    RatedShortcut
} from "../../../event/consts.js";
import {openWindow} from "../../../http/WindowOpener.jsx";
import {OrderChangesHistoryModal} from "../../../admin/AdminOrders/OrderChangesHistoryModal.jsx";
import {NOOP} from "../../../utils/NOOP.jsx";
import {OrderStatus, StyledButton} from "../../../components/index.jsx";
import {UpdateOrderStatusModal} from "../../../admin/AdminOrders/UpdateOrderModal/index.js";
import {isAllowedToManageOrder} from "./utils.js";
import {getToastMessageToExcelOrders} from "../../../admin/AdminOrders/utils.jsx";
import {acceptOrder, deliveredOrder} from "../../../api/orders/orders.js";
import {
    updateOrder,
    changeOrderStatus,
    cancelSystemOrder,
    sendReminderToProvider,
    reviewBadRating
} from "../../../api/orders/systemOrders.js";
import {DeclineOrderModal} from "../../../event/DeclineOrderModal/DeclineOrderModal.jsx";
import {HttpClient} from "../../../http/HttpClient.jsx";
import {
    filterOrdersBySearchInput,
    generateAdminOrderTableActions,
    generateProviderReminderOperationActions
} from "../../../admin/AdminOrders/utils.jsx";
import {ProductBookingAsClientModal} from "../../../marketplace/MarketplaceBooking/ProductBooking/ProductBookingAsClient/ProductBookingAsClientModal.jsx";
import "./system-orders.css";
import {OrderTableColumns} from "../../../admin/AdminOrders/consts.jsx";
import {createOrderIssueLog, updateOrderIssueLog, reviewDecline} from "../../../api/orders/systemOrders.js";
import {IssueModal} from "../../../event/IssueModal/IssueModal.jsx";
import {OrderIssueLogsModal} from "../../../event/IssueModal/OrderIssueLogsModal.jsx";
import {useListenToSocket} from "../../../components/socket/useListenToSocket.jsx";

export const SystemOrders = () => {
    const {me, services} = useContext(AppContext);
    const {searchDate, searchByCreated, searchStatuses, formValues} = useContext(TableDateSearchContext);

    const [searchInput, setSearchInput] = useState(null);
    const [historyOrder, setHistoryOrder] = useState(null);
    const [updateOrderStatusInfo, setUpdateOrderStatusInfo] = useState(null);
    const [declineOrder, setDeclineOrder] = useState(null);
    const [visibleCreateOrderAsClient, setVisibleCreateOrderAsClient] = useState(false);
    const [issueModalOrder, setIssueModalOrder] = useState(null);

    const openIssuedOrders = formValues?.selectedOption === OrderShortcutFilterValues.OPEN_ISSUES;
    const ratedOrders = formValues?.selectedOption === OrderShortcutFilterValues.RATED;

    const baseShortcutOptions = useMemo(() => [...OrdersShortcuts, OpenIssuesShortcut, RatedShortcut], []);
    const [summary, _loadingSummary, _errSummary, fetchSummary] = useRequest(
        "/api/system/orderShortcutSummary?key=order-management",
        "get",
        null,
        [],
        true
    );

    useListenToSocket({
        eventName: ORDER_SHORTCUT_SUMMARY_UPDATED_EVENT,
        onUpdate: () => fetchSummary(),
        room: ORDER_SHORTCUT_SUMMARY_ROOM
    });

    const shortcutSearchOptions = useMemo(
        () =>
            baseShortcutOptions.map(opt => ({
                ...opt,
                notificationCount: summary?.summary?.find(s => s.key === opt.key)?.count ?? 0
            })),
        [baseShortcutOptions, summary]
    );

    const [orders, isLoadingOrders, _isErrorOnLoadingOrders, fetchOrders] = useRequest(
        "/api/events/system/myOrders",
        "POST",
        {
            startTime: searchDate?.[0].valueOf(),
            endTime: searchDate?.[1].valueOf(),
            byCreatedAt: searchByCreated,
            ...(searchStatuses?.length ? {statuses: searchStatuses} : {}),
            ...(openIssuedOrders ? {openIssuedOrders: true} : {}),
            ...(ratedOrders ? {ratedOrders: true} : {})
        },
        [searchDate, searchByCreated, searchStatuses, openIssuedOrders, ratedOrders],
        searchDate?.length
    );

    const onChangeOrderStatus = useCallback(
        async (event, status, toRemove) => {
            let shouldSyncAllOrders = true;
            try {
                if (toRemove) {
                    await changeOrderStatus(event, status, toRemove);
                } else {
                    switch (status) {
                        case OrderStatus.accepted: {
                            await acceptOrder(event);
                            break;
                        }
                        case OrderStatus.delivered: {
                            await deliveredOrder(event);
                            break;
                        }
                        case OrderStatus.cancelled: {
                            await cancelSystemOrder(event);
                            getToastMessageToExcelOrders(status, event);
                            break;
                        }
                        case OrderStatus.declined: {
                            setDeclineOrder(event);
                            shouldSyncAllOrders = false;
                            break;
                        }
                        default: {
                            await changeOrderStatus(event, status, toRemove);
                            break;
                        }
                    }
                }

                if (shouldSyncAllOrders) {
                    fetchOrders();
                }
            } catch (e) {
                message.error(e.message, 3);
            }
            setUpdateOrderStatusInfo(null);
        },
        [fetchOrders]
    );

    const [systemUsers] = useRequest("/api/users/systemStaffUsers");

    const onCommentSave = useCallback(
        async (order, commentText) => {
            const adminComment = commentText
                ? {
                      content: commentText,
                      author: me.firstName,
                      lastUpdate: Date.now()
                  }
                : null;

            const orderToUpdate = {
                eventId: order.eventId,
                adminComment
            };

            const {error} = await updateOrder(
                orderToUpdate,
                "none",
                order.updatedAt,
                "שגיאה, ההערה על ההזמנה לא נשמרה",
                "ההערה על ההזמנה נשמרה בהצלחה"
            );

            if (error) {
                return;
            }

            fetchOrders();
        },
        [fetchOrders, me.firstName]
    );

    const declineOffer = useCallback(
        async ({declineReason, declineComment}) => {
            try {
                const {error} = await HttpClient.post(`/api/events/${declineOrder.eventId}/declineV2`, {
                    declineReason,
                    declineComment,
                    updatedAt: declineOrder.updatedAt
                });

                if (error) {
                    message.error(error, 3);
                } else {
                    message.success("ההזמנה השתנתה בהצלחה.");
                    getToastMessageToExcelOrders(OrderStatus.declined, declineOrder);
                }

                fetchOrders();
            } catch (e) {
                message.error(e.message, 3);
            }
            setDeclineOrder(null);
        },
        [declineOrder, fetchOrders]
    );

    const onSaveIssue = useCallback(
        async issue => {
            if (issue.logId) {
                await updateOrderIssueLog({...issue, eventId: issueModalOrder.eventId});
            } else {
                await createOrderIssueLog({...issue, eventId: issueModalOrder.eventId});
            }
            setIssueModalOrder(null);
            fetchOrders();
        },
        [fetchOrders, issueModalOrder]
    );

    const handleSendReminderToProvider = useCallback(
        async order => {
            try {
                const response = await sendReminderToProvider(order);
                if (response?.error) {
                    message.error(response.error, 3);
                } else {
                    message.success("התזכורת נשלחה לספק בהצלחה");
                    fetchOrders(); // Refresh to show updated count
                }
            } catch (e) {
                message.error(e.message, 3);
            }
        },
        [fetchOrders]
    );

    const operationActions = useMemo(
        () =>
            generateProviderReminderOperationActions(
                me,
                handleSendReminderToProvider,
                setDeclineOrder,
                setIssueModalOrder,
                null,
                null,
                order => onDeclineReview(order),
                order => onBadRatingIssueModalOpen(order)
            ),
        [me, handleSendReminderToProvider, setDeclineOrder, setIssueModalOrder]
    );

    const onDeclineReview = useCallback(
        async order => {
            await reviewDecline(order);
            fetchOrders();
        },
        [fetchOrders]
    );

    const onBadRatingIssueModalOpen = useCallback(
        async order => {
            setIssueModalOrder(order);
            await reviewBadRating(order);
            fetchOrders();
        },
        [fetchOrders, setIssueModalOrder]
    );

    const filteredTransactions = useMemo(() => filterOrdersBySearchInput(orders, searchInput), [orders, searchInput]);

    return (
        <div className="system-orders-page">
            <ConfigProvider direction={"rtl"}>
                <ProductBookingAsClientModal
                    visible={visibleCreateOrderAsClient}
                    onClose={() => setVisibleCreateOrderAsClient(false)}
                />

                <OrderChangesHistoryModal
                    visible={!!historyOrder}
                    orderId={historyOrder?.eventId}
                    onClose={() => setHistoryOrder(null)}
                    isAdmin={false}
                />

                <UpdateOrderStatusModal
                    setUpdateOrderStatusInfo={setUpdateOrderStatusInfo}
                    updateOrderStatusInfo={updateOrderStatusInfo}
                    onChangeOrderStatus={onChangeOrderStatus}
                />

                <DeclineOrderModal
                    visible={!!declineOrder}
                    onSave={declineOffer}
                    onClose={() => setDeclineOrder(null)}
                />

                <OrderIssueLogsModal
                    visible={!!issueModalOrder}
                    onClose={() => setIssueModalOrder(null)}
                    onSave={onSaveIssue}
                    order={issueModalOrder}
                />
            </ConfigProvider>

            <TableDateRangeTable
                title="ניהול הזמנות"
                subTitle="ריכוז כלל ההזמנות לצורך ניהול, מעקב ובקרה"
                data={filteredTransactions}
                onInputSearch={value => setSearchInput(value)}
                isLoadingOrderTable={isLoadingOrders}
                statisticsLabel={
                    <StyledButton className="create-order-btn" onClick={() => setVisibleCreateOrderAsClient(true)}>
                        יצירת הזמנה
                    </StyledButton>
                }
                enforceMaxDateRange={true}
                maxDateRangeUnit="week"
                actions={generateAdminOrderTableActions(
                    me,
                    isAllowedToManageOrder(me) ? order => openWindow(`/events/system/${order.eventId}/edit`) : NOOP,
                    isAllowedToManageOrder(me)
                        ? (order, status, toRemove) =>
                              setUpdateOrderStatusInfo({
                                  event: order,
                                  status,
                                  toRemove
                              })
                        : NOOP,
                    order => setHistoryOrder(order),
                    NOOP,
                    NOOP,
                    NOOP,
                    NOOP,
                    order => setIssueModalOrder(order)
                )}
                columns={columns(
                    me,
                    services,
                    isAllowedToManageOrder(me) ? order => openWindow(`/events/system/${order.eventId}/edit`) : NOOP,
                    isAllowedToManageOrder(me)
                        ? (order, status, toRemove) =>
                              setUpdateOrderStatusInfo({
                                  event: order,
                                  status,
                                  toRemove
                              })
                        : NOOP,
                    () => fetchOrders(),
                    isAllowedToManageOrder(me) ? systemUsers : [],
                    NOOP,
                    NOOP,
                    order => setHistoryOrder(order),
                    NOOP,
                    true,
                    null,
                    NOOP,
                    onCommentSave,
                    null,
                    NOOP,
                    NOOP,
                    operationActions
                )}
                shortcutSearchOptions={shortcutSearchOptions}
                hideColumns={[
                    OrderTableColumns.assignedPartners,
                    OrderTableColumns.tip,
                    OrderTableColumns.providerDiscount,
                    OrderTableColumns.discount,
                    OrderTableColumns.actions,
                    OrderTableColumns.deliveryProviderDiscount
                ]}
            />
        </div>
    );
};
